import {
  Car,
  Home,
  Wrench,
  Sparkles,
  Shield,
  Package,
  Zap,
  Scale,
  Megaphone,
  FileText,
  Receipt,
  TrendingDown,
  UtensilsCrossed,
  DollarSign,
  Globe,
  ArrowDownLeft,
  ArrowUpRight,
} from "lucide-react";

/**
 * Exact color and icon mapping for transaction categories
 * Used across the transaction list, dashboard charts, and category summaries.
 */
export const CATEGORY_CONFIG = {
  // Expense Categories
  "Auto & Travel": {
    color: "#3B82F6",
    icon: Car,
    label: "Auto & Travel",
  },
  "Mortgage Interest": {
    color: "#1C3A5E",
    icon: Home,
    label: "Mortgage Interest",
  },
  "Other Interest": {
    color: "#1E40AF",
    icon: Home,
    label: "Other Interest",
  },
  "Repairs": {
    color: "#F97316",
    icon: Wrench,
    label: "Repairs",
  },
  "Cleaning & Maintenance": {
    color: "#06B6D4",
    icon: Sparkles,
    label: "Cleaning & Maintenance",
  },
  "Insurance": {
    color: "#0D9488",
    icon: Shield,
    label: "Insurance",
  },
  "Supplies": {
    color: "#10B981",
    icon: Package,
    label: "Supplies",
  },
  "Utilities": {
    color: "#8B5CF6",
    icon: Zap,
    label: "Utilities",
  },
  "Legal & Professional": {
    color: "#6366F1",
    icon: Scale,
    label: "Legal & Professional",
  },
  "Advertising": {
    color: "#EC4899",
    icon: Megaphone,
    label: "Advertising",
  },
  "Management Fees": {
    color: "#14B8A6",
    icon: FileText,
    label: "Management Fees",
  },
  "Taxes": {
    color: "#EF4444",
    icon: Receipt,
    label: "Taxes",
  },
  "Depreciation": {
    color: "#6B7280",
    icon: TrendingDown,
    label: "Depreciation",
  },
  "Meals & Entertainment": {
    color: "#F59E0B",
    icon: UtensilsCrossed,
    label: "Meals & Entertainment",
  },
  "Commissions": {
    color: "#84CC16",
    icon: DollarSign,
    label: "Commissions",
  },
  "Consumables/Toiletries": {
    color: "#FB7185",
    icon: Package,
    label: "Consumables/Toiletries",
  },
  "Professional Cleaning": {
    color: "#22D3EE",
    icon: Sparkles,
    label: "Professional Cleaning",
  },
  "Platform Fees": {
    color: "#A78BFA",
    icon: Globe,
    label: "Platform Fees",
  },
  "Guest Supplies": {
    color: "#34D399",
    icon: Package,
    label: "Guest Supplies",
  },
  "Capital Contribution": {
    color: "#059669",
    icon: ArrowDownLeft,
    label: "Capital Contribution",
  },
  "Owner Draw": {
    color: "#DC2626",
    icon: ArrowUpRight,
    label: "Owner Draw",
  },
  "Due to Member": {
    color: "#D97706",
    icon: DollarSign,
    label: "Due to Member",
  },
  "Other": {
    color: "#6B7280",
    icon: DollarSign,
    label: "Other",
  },

  // Income Categories
  "Rent": {
    color: "#10B981",
    icon: ArrowUpRight,
    label: "Rent",
  },
  "STR Booking": {
    color: "#10B981",
    icon: ArrowUpRight,
    label: "STR Booking",
  },
  "Income": {
    color: "#10B981",
    icon: ArrowUpRight,
    label: "Income",
  },
};

const DEFAULT_EXPENSE_CONFIG = {
  color: "#6B7280",
  icon: DollarSign,
  label: "Other",
};

const DEFAULT_INCOME_CONFIG = {
  color: "#10B981",
  icon: ArrowUpRight,
  label: "Income",
};

/**
 * Retrieves the color and icon configuration for a given transaction category and type.
 * @param {string} category Category name
 * @param {string} [type] "Income" or "Expense"
 * @returns {{ color: string, icon: any, label: string }}
 */
export function getCategoryConfig(category, type) {
  if (type === "Income") {
    if (category && CATEGORY_CONFIG[category]) {
      return CATEGORY_CONFIG[category];
    }
    return DEFAULT_INCOME_CONFIG;
  }

  if (category && CATEGORY_CONFIG[category]) {
    return CATEGORY_CONFIG[category];
  }

  // Fallback matching by keyword
  const cat = (category || "").toLowerCase();
  if (cat.includes("auto") || cat.includes("travel") || cat.includes("mileage")) {
    return CATEGORY_CONFIG["Auto & Travel"];
  }
  if (cat.includes("mortgage") || (cat.includes("interest") && !cat.includes("other"))) {
    return CATEGORY_CONFIG["Mortgage Interest"];
  }
  if (cat.includes("other interest")) {
    return CATEGORY_CONFIG["Other Interest"];
  }
  if (cat.includes("professional cleaning")) {
    return CATEGORY_CONFIG["Professional Cleaning"];
  }
  if (cat.includes("cleaning") || cat.includes("maintenance")) {
    return CATEGORY_CONFIG["Cleaning & Maintenance"];
  }
  if (cat.includes("repair")) {
    return CATEGORY_CONFIG["Repairs"];
  }
  if (cat.includes("insurance")) {
    return CATEGORY_CONFIG["Insurance"];
  }
  if (cat.includes("consumable") || cat.includes("toiletr")) {
    return CATEGORY_CONFIG["Consumables/Toiletries"];
  }
  if (cat.includes("guest suppl")) {
    return CATEGORY_CONFIG["Guest Supplies"];
  }
  if (cat.includes("suppl") || cat.includes("hardware")) {
    return CATEGORY_CONFIG["Supplies"];
  }
  if (cat.includes("utilit") || cat.includes("electric") || cat.includes("water") || cat.includes("gas") || cat.includes("trash")) {
    return CATEGORY_CONFIG["Utilities"];
  }
  if (cat.includes("legal") || cat.includes("professional")) {
    return CATEGORY_CONFIG["Legal & Professional"];
  }
  if (cat.includes("advertising") || cat.includes("market")) {
    return CATEGORY_CONFIG["Advertising"];
  }
  if (cat.includes("management")) {
    return CATEGORY_CONFIG["Management Fees"];
  }
  if (cat.includes("tax")) {
    return CATEGORY_CONFIG["Taxes"];
  }
  if (cat.includes("depreciation")) {
    return CATEGORY_CONFIG["Depreciation"];
  }
  if (cat.includes("meal") || cat.includes("entertain") || cat.includes("food")) {
    return CATEGORY_CONFIG["Meals & Entertainment"];
  }
  if (cat.includes("commission")) {
    return CATEGORY_CONFIG["Commissions"];
  }
  if (cat.includes("platform")) {
    return CATEGORY_CONFIG["Platform Fees"];
  }
  if (cat.includes("capital") || cat.includes("contribution")) {
    return CATEGORY_CONFIG["Capital Contribution"];
  }
  if (cat.includes("draw")) {
    return CATEGORY_CONFIG["Owner Draw"];
  }
  if (cat.includes("due to member")) {
    return CATEGORY_CONFIG["Due to Member"];
  }

  return {
    ...DEFAULT_EXPENSE_CONFIG,
    label: category || "Other",
  };
}
